import {
    put, call, all, takeLatest, race, delay,
} from 'redux-saga/effects';
import { notification } from 'antd';
import {
    signInFailure, signInSuccess, signUpSuccess, signUpFailure, authChallenge
} from '../redux/actions/authActions';
import { SIGNIN, SIGNOUT, SIGNOUT_SAGA, SIGNUP } from '../redux/types';
import { setTokens, userSignIn, userSignOut, userSignUp, clearTokens } from '../services/auth';
import { parseJwt } from '../utils/utility';
import { getUserAccessPermissions } from '../redux/UserManagement/actions';
// Global flag to prevent multiple signOut executions
let isSignOutInProgress = false;

function* signIn({ credentials }) {
    try {
        const res = yield userSignIn(credentials.username?.trim(), credentials.password?.trim());
       
        notification.destroy();
        
        // Check if there's a challenge (like NEW_PASSWORD_REQUIRED)
        if (res && res.challengeName) {
            yield put(authChallenge(res.challengeName, res));
            return;
        }
        
        if (res && !res.challengeName) {
            localStorage.setItem('user', JSON.stringify(res.attributes))
            notification.success({ message: 'Login Successful.' });
            yield put(getUserAccessPermissions(res.attributes.sub));

            yield setTokens(res);
        }

        yield put(signInSuccess({ res }));

    } catch (error) {
        yield put(signInFailure());
        notification.destroy();
        if (error.message === 'Network error') {
            notification.error({
                message: error.message
            });
        } else if (error.message === "User is not confirmed.") {
            notification.warn({
                message: error.message,
                description: "Please verify your email & try again."
            });
        } else if (error.message === "An account with the given email already exists.") {
            notification.warn({
                message: error.message,
                description: "Please try again with another email."
            });
        } else {
            notification.error({
                message: 'Incorrect email address or password',
            });
        }
    }
}

export const getCurrentUser = () => {
    const token = localStorage.getItem('ap_idToken') ?? '';
    if (token) {
        const userPayload = parseJwt(token);
        if (userPayload.exp < new Date().getTime() / 1000) {
            return false;
        }
        return true;
    }
    return false;
};


function* signOut() {
    // Prevent multiple executions
    if (isSignOutInProgress) {
        return;
    }
    
    isSignOutInProgress = true;
    
    // Add a guard to prevent multiple executions
    const sagaId = Date.now();
    
    try {
        // Clear tokens from localStorage first
        yield call(clearTokens);
        
        // Dispatch SIGNOUT action to update Redux state
        yield put({ type: SIGNOUT });
        
        notification.success({ message: 'Signed out successfully.' });
        
        // Try to clear AWS session with a timeout
        try {
            const { result, timeout } = yield race({
                result: call(userSignOut),
                timeout: delay(5000) // 5 second timeout
            });
            
            if (timeout) {
                // AWS signOut timed out, continuing with logout
            } else {
                // AWS signOut completed successfully
            }
        } catch (awsError) {
            // AWS signOut error (non-blocking)
        }
        
    } catch (error) {
        // Even if there's an error, clear the state
        yield call(clearTokens);
        yield put({ type: SIGNOUT });
        notification.error({ message: 'Error signing out.' });
    } finally {
        // Reset the flag
        isSignOutInProgress = false;
    }
}

function* signUp({ credentials }) {
    try {
        var attributes = {}
        attributes.firstName = credentials.firstName
        attributes.lastName = credentials.lastName
        const res = yield userSignUp(credentials.username?.trim(), credentials.password?.trim(), attributes);

        notification.destroy();
        if (res) {
            notification.destroy();
            notification.success({
                duration: 5,
                message: 'Successfully Created PIH Account.',
                description: 'Please Verify Your mail & Login'

            });
            yield put(signUpSuccess({ res }));
        }

    } catch (error) {
        yield put(signUpFailure());
        notification.destroy();

        if (error.message === 'Network error') {
            notification.error({
                message: error.message
            });
        } else if (error.message === 'An account with the given email already exists.') {
            notification.error({
                message: error.message,
            });
            notification.warn({
                message: "Please try signing up using different email.",
            });
        } else {
            notification.error({
                message: 'Incorrect email address or password',
            });
        }
    }
}

function* authWatcher() {
    yield takeLatest(SIGNIN, signIn);
    yield takeLatest(SIGNOUT_SAGA, signOut);
    yield takeLatest(SIGNUP, signUp);
}

function* authSagas() {
    yield all([
        call(authWatcher),
    ]);
}

export default authSagas;
